/* DS-03: Canonical branding token applicator.
   Single source of truth for all CSS custom-property injection across
   auth (Layer 1), org live-preview (Layer 2), and workspace WL (Layer 3). */

const BRANDING_TOKENS = [
  '--nx-brand-color', '--nx-accent',
  '--nx-accent-hover', '--nx-accent-active', '--nx-accent-tint', '--nx-accent-tint-2',
  '--nx-success', '--nx-success-tint',
  '--nx-warning', '--nx-warning-tint',
  '--nx-danger',  '--nx-danger-tint',
  '--nx-font',
];

/* Apply branding config as CSS custom properties on :root.
   config shape (all fields optional):
     primaryColor | primary  — brand / accent base color
     accentColor             — override for interactive accent (falls back to primary)
     secondaryColor          — tint / background accent
     successColor, warningColor, dangerColor
     font                    — Google font family name */
export function applyBrandingTokens(config) {
  if (!config) return;
  const root = document.documentElement;

  const primary = config.primaryColor ?? config.primary ?? null;
  const accent  = config.accentColor  || primary;

  if (primary) root.style.setProperty('--nx-brand-color', primary);
  if (accent) {
    root.style.setProperty('--nx-accent',        accent);
    root.style.setProperty('--nx-accent-hover',  `color-mix(in srgb, ${accent} 75%, var(--nx-bg, #fff))`);
    root.style.setProperty('--nx-accent-active', `color-mix(in srgb, ${accent} 65%, #000)`);
    root.style.setProperty('--nx-accent-tint-2', `color-mix(in srgb, ${accent} 5%,  var(--nx-bg, #fff))`);
  }
  const tint = config.secondaryColor
    || (primary ? `color-mix(in srgb, ${primary} 12%, var(--nx-bg, #fff))` : null);
  if (tint) root.style.setProperty('--nx-accent-tint', tint);

  if (config.successColor) {
    root.style.setProperty('--nx-success',      config.successColor);
    root.style.setProperty('--nx-success-tint', `color-mix(in srgb, ${config.successColor} 10%, transparent)`);
  }
  if (config.warningColor) {
    root.style.setProperty('--nx-warning',      config.warningColor);
    root.style.setProperty('--nx-warning-tint', `color-mix(in srgb, ${config.warningColor} 12%, transparent)`);
  }
  if (config.dangerColor) {
    root.style.setProperty('--nx-danger',       config.dangerColor);
    root.style.setProperty('--nx-danger-tint',  `color-mix(in srgb, ${config.dangerColor} 10%, transparent)`);
  }
  if (config.font) {
    root.style.setProperty(
      '--nx-font',
      `"${config.font}", ui-sans-serif, system-ui, -apple-system, sans-serif`,
    );
  }
}

/* Remove all branding token overrides, reverting to :root CSS defaults. */
export function clearBrandingTokens() {
  const root = document.documentElement;
  BRANDING_TOKENS.forEach(t => root.style.removeProperty(t));
}

/* Last workspace branding applied via applyBrandingGlobally.
   Set whenever workspace branding is applied; read by OrganizationLayout cleanup
   to restore workspace branding immediately (before paint) when the Org Console
   unmounts — eliminating the CSS-fallback BLUE that appears between OrgLayout
   clearing vars and WorkspaceShell/WorkspaceConsoleLayout restoring them. */
let _lastWorkspaceBranding = null;
export const setLastWorkspaceBranding = (b) => { _lastWorkspaceBranding = b; };
export const getLastWorkspaceBranding = () => _lastWorkspaceBranding;
